import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import './Leaderboard.css';

interface LeaderboardPerson {
  id: number;
  name: string;
  totalSpent: number;
  totalSavings: number;
  purchaseCount: number;
  avgSavingsPerDrink: number;
}

interface LeaderboardData {
  bestSavers: LeaderboardPerson[];
  biggestSpenders: LeaderboardPerson[];
  highestVolume: LeaderboardPerson[];
  bestAvgSavings: LeaderboardPerson[];
  worstSavers: LeaderboardPerson[];
}

interface PersonStats {
  person: any;
  totalSpent: number;
  totalSavings: number;
  purchaseCount: number;
  drinkStats: any[];
  recentPurchases: any[];
}

interface LeaderboardProps {
  persons?: any[];
  salesHistory?: any[];
}

const Leaderboard: React.FC<LeaderboardProps> = ({ persons, salesHistory }) => {
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardData | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<number | null>(null);
  const [personStats, setPersonStats] = useState<PersonStats | null>(null);
  const [activeTab, setActiveTab] = useState<'bestSavers' | 'biggestSpenders' | 'highestVolume' | 'bestAvgSavings' | 'worstSavers'>('bestSavers');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLeaderboardData();
    
    // Connect to socket for systemReset events
    const newSocket = io('https://boersensaufen.fwne.de:1234');
    
    newSocket.on('systemReset', () => {
      // Clear local state on system reset
      setLeaderboardData(null);
      setSelectedPerson(null);
      setPersonStats(null);
      // Fetch fresh data after reset
      setTimeout(() => {
        fetchLeaderboardData();
      }, 100);
    });
    
    return () => {
      newSocket.close();
    };
  }, []);

  const fetchLeaderboardData = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://boersensaufen.fwne.de:1234/api/leaderboard');
      const data = await response.json();
      setLeaderboardData(data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonStats = async (personId: number) => {
    setLoading(true);
    try {
      const response = await fetch(`https://boersensaufen.fwne.de:1234/api/persons/${personId}/stats`);
      const data = await response.json();
      setPersonStats(data);
      setSelectedPerson(personId);
    } catch (error) {
      console.error('Error fetching person stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderLeaderboardList = (persons: LeaderboardPerson[], title: string, valueKey: 'totalSavings' | 'totalSpent' | 'purchaseCount' | 'avgSavingsPerDrink') => {
    if (!persons || persons.length === 0) {
      return (
        <div className="empty-list">
          <p>Noch keine Daten verfügbar</p>
        </div>
      );
    }

    const getValueDisplay = (person: LeaderboardPerson) => {
      switch (valueKey) {
        case 'totalSavings':
          return `€${person.totalSavings.toFixed(2)}`;
        case 'totalSpent':
          return `€${person.totalSpent.toFixed(2)}`;
        case 'purchaseCount':
          return `${person.purchaseCount} Getränke`;
        case 'avgSavingsPerDrink':
          return `€${person.avgSavingsPerDrink.toFixed(2)}/Getränk`;
        default:
          return '';
      }
    };

    const getValueClass = (person: LeaderboardPerson) => {
      if (valueKey === 'totalSavings' || valueKey === 'avgSavingsPerDrink') {
        return person[valueKey] >= 0 ? 'positive' : 'negative';
      }
      return '';
    };

    return (
      <div className="leaderboard-list">
        <h3>{title}</h3>
        {persons.map((person, index) => (
          <div
            key={person.id}
            className="leaderboard-item"
            onClick={() => fetchPersonStats(person.id)}
          >
            <div className="rank">#{index + 1}</div>
            <div className="person-info">
              <div className="person-name">{person.name}</div>
              <div className="person-summary">
                {person.purchaseCount} Käufe | €{person.totalSpent.toFixed(2)} ausgegeben
              </div>
            </div>
            <div className={`person-value ${getValueClass(person)}`}>
              {getValueDisplay(person)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPersonDetails = () => {
    if (!personStats) return null;

    return (
      <div className="person-details">
        <div className="details-header">
          <h3>🏆 {personStats.person.name} - Detailansicht</h3>
          <button
            className="close-button"
            onClick={() => {
              setSelectedPerson(null);
              setPersonStats(null);
            }}
          >
            ✕
          </button>
        </div>
        
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-value">€{personStats.totalSpent.toFixed(2)}</div>
            <div className="stat-label">Gesamt ausgegeben</div>
          </div>
          <div className="stat-card">
            <div className={`stat-value ${personStats.totalSavings >= 0 ? 'positive' : 'negative'}`}>
              €{personStats.totalSavings.toFixed(2)}
            </div>
            <div className="stat-label">Gespart/Verloren</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{personStats.purchaseCount}</div>
            <div className="stat-label">Getränke gekauft</div>
          </div>
          <div className="stat-card">
            <div className={`stat-value ${personStats.purchaseCount > 0 && personStats.totalSavings / personStats.purchaseCount >= 0 ? 'positive' : 'negative'}`}>
              €{personStats.purchaseCount > 0 ? (personStats.totalSavings / personStats.purchaseCount).toFixed(2) : '0.00'}
            </div>
            <div className="stat-label">Ø Gespart pro Getränk</div>
          </div>
        </div>

        {personStats.drinkStats && personStats.drinkStats.length > 0 && (
          <div className="drink-stats">
            <h4>Getränke-Statistiken</h4>
            <div className="drink-stats-list">
              {personStats.drinkStats.map(drink => (
                <div key={drink.drinkName} className="drink-stat-item">
                  <div className="drink-icon">{drink.drinkIcon}</div>
                  <div className="drink-info">
                    <div className="drink-name">{drink.drinkName}</div>
                    <div className="drink-details">
                      {drink.quantity}x gekauft | Ø €{drink.avgPrice.toFixed(2)} 
                      (Basis: €{drink.basePrice.toFixed(2)})
                    </div>
                  </div>
                  <div className={`drink-savings ${drink.totalSavings >= 0 ? 'positive' : 'negative'}`}>
                    {drink.totalSavings >= 0 ? '+' : ''}€{drink.totalSavings.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {personStats.recentPurchases && personStats.recentPurchases.length > 0 && (
          <div className="recent-purchases">
            <h4>Letzte Käufe</h4>
            <div className="purchases-list">
              {personStats.recentPurchases.map((purchase, index) => (
                <div key={index} className="purchase-item">
                  <div className="purchase-time">
                    {new Date(purchase.timestamp).toLocaleString('de-DE')}
                  </div>
                  <div className="purchase-details">
                    {purchase.quantity}x {purchase.drinkName} - €{purchase.priceAtSale.toFixed(2)}
                  </div>
                  <div className="purchase-total">
                    €{purchase.totalCost.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading && !leaderboardData) {
    return (
      <div className="leaderboard">
        <h2>🏆 Bestenliste</h2>
        <div className="loading">Lade Bestenliste...</div>
      </div>
    );
  }

  if (selectedPerson && personStats) {
    return (
      <div className="leaderboard">
        <h2>🏆 Bestenliste</h2>
        {renderPersonDetails()}
      </div>
    );
  }

  return (
    <div className="leaderboard">
      <h2>🏆 Bestenliste</h2>
      
      <div className="refresh-button-container">
        <button className="refresh-button" onClick={fetchLeaderboardData} disabled={loading}>
          {loading ? '🔄 Aktualisiere...' : '🔄 Aktualisieren'}
        </button>
      </div>

      <div className="tabs">
        <button
          className={activeTab === 'bestSavers' ? 'active' : ''}
          onClick={() => setActiveTab('bestSavers')}
        >
          💰 Beste Sparer
        </button>
        <button
          className={activeTab === 'biggestSpenders' ? 'active' : ''}
          onClick={() => setActiveTab('biggestSpenders')}
        >
          🛍️ Größte Ausgaben
        </button>
        <button
          className={activeTab === 'highestVolume' ? 'active' : ''}
          onClick={() => setActiveTab('highestVolume')}
        >
          📊 Meiste Käufe
        </button>
        <button
          className={activeTab === 'bestAvgSavings' ? 'active' : ''}
          onClick={() => setActiveTab('bestAvgSavings')}
        >
          📈 Bester Ø-Einkauf
        </button>
        <button
          className={activeTab === 'worstSavers' ? 'active' : ''}
          onClick={() => setActiveTab('worstSavers')}
        >
          📉 Schlechteste Sparer
        </button>
      </div>

      <div className="leaderboard-content">
        {leaderboardData && (
          <>
            {activeTab === 'bestSavers' && renderLeaderboardList(leaderboardData.bestSavers, '💰 Die besten Sparer', 'totalSavings')}
            {activeTab === 'biggestSpenders' && renderLeaderboardList(leaderboardData.biggestSpenders, '🛍️ Die größten Ausgaben', 'totalSpent')}
            {activeTab === 'highestVolume' && renderLeaderboardList(leaderboardData.highestVolume, '📊 Die meisten Käufe', 'purchaseCount')}
            {activeTab === 'bestAvgSavings' && renderLeaderboardList(leaderboardData.bestAvgSavings, '📈 Bester Durchschnittseinkauf', 'avgSavingsPerDrink')}
            {activeTab === 'worstSavers' && renderLeaderboardList(leaderboardData.worstSavers, '📉 Die schlechtesten Sparer', 'totalSavings')}
          </>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;